import { skipToken } from "@reduxjs/toolkit/query";
import {
  nextInterviewQuestionsDataExtractionDisplayName,
  nextInterviewQuestionsDataExtractionFieldName,
  nextInterviewQuestionsDataExtractionSentinelId,
} from "@repo/app-types/entities/nextInterviewQuestions";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  useAllDataExtractionFieldDefinitionsQuery,
  useDataExtractionFieldDefinitionsByJobTypeIdQuery,
  useUpdateDataExtractionFieldDefinitionsForJobTypeMutation,
} from "../../../api/entities/dataExtractionField.js";
import { appPaths } from "../../../app/appPaths.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";

export interface DataExtractionJobTypeSettingProps {
  jobTypeId: string;
}

interface DataExtractionFieldOption {
  id: string;
  name: string;
}

export const DataExtractionJobTypeSetting: FC<
  DataExtractionJobTypeSettingProps
> = ({ jobTypeId }) => {
  // Fetch all available data extraction field definitions
  const { data: allDataExtractionFieldDefinitions, isLoading: isLoadingAll } =
    useAllDataExtractionFieldDefinitionsQuery();

  const navigate = useNavigate();

  // Fetch data extraction field definition IDs for this job type
  const {
    data: jobTypeDataExtractionFieldDefinitionIds,
    isLoading: isLoadingJobType,
  } = useDataExtractionFieldDefinitionsByJobTypeIdQuery(
    jobTypeId ? { jobTypeId } : skipToken,
  );

  // Mutation to update the job type's data extraction field definitions
  const [updateFieldDefinitions, { isLoading: isUpdating }] =
    useUpdateDataExtractionFieldDefinitionsForJobTypeMutation();

  // State to track selected field definition IDs
  const [selectedFieldDefinitionIds, setSelectedFieldDefinitionIds] = useState<
    string[]
  >([]);

  // Initialize selected field definition IDs when data is loaded
  useEffect(() => {
    if (jobTypeDataExtractionFieldDefinitionIds) {
      setSelectedFieldDefinitionIds(jobTypeDataExtractionFieldDefinitionIds);
    }
  }, [jobTypeDataExtractionFieldDefinitionIds]);

  // Check if there are unsaved changes
  const hasChanges =
    jobTypeDataExtractionFieldDefinitionIds &&
    (selectedFieldDefinitionIds.length !==
      jobTypeDataExtractionFieldDefinitionIds.length ||
      !selectedFieldDefinitionIds.every((id) =>
        jobTypeDataExtractionFieldDefinitionIds.includes(id),
      ));

  const handleSave = async () => {
    if (!jobTypeId) return;

    try {
      await updateFieldDefinitions({
        jobTypeId,
        customFieldDefinitionIds: selectedFieldDefinitionIds,
      }).unwrap();
    } catch (error) {
      console.error(
        "Failed to update data extraction field definitions",
        error,
      );
    }
  };

  const handleCancel = () => {
    if (jobTypeDataExtractionFieldDefinitionIds) {
      setSelectedFieldDefinitionIds(jobTypeDataExtractionFieldDefinitionIds);
    }
  };

  const isLoading = isLoadingAll || isLoadingJobType || isUpdating;

  // The "Next interview questions" system field is materialized on-demand
  // (server-side, when the user saves). Until that happens, surface it in
  // the picker via a sentinel id so it's selectable.
  const realFieldDefinitions = allDataExtractionFieldDefinitions ?? [];
  const isNextInterviewQuestionsAlreadyMaterialized = realFieldDefinitions.some(
    (def) => def.field_name === nextInterviewQuestionsDataExtractionFieldName,
  );
  const dropdownOptions: DataExtractionFieldOption[] = [
    ...realFieldDefinitions.map((def) => ({
      id: def.id,
      name: def.display_name,
    })),
    ...(isNextInterviewQuestionsAlreadyMaterialized
      ? []
      : [
          {
            id: nextInterviewQuestionsDataExtractionSentinelId,
            name: nextInterviewQuestionsDataExtractionDisplayName,
          },
        ]),
  ];

  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.JOB_OPENINGS}
      passThrough={true}
    >
      {(allowed) => (
        <div className="mb-6">
          <div className="w-full max-w-lg">
            <ComboboxField<DataExtractionFieldOption, "multiple">
              label="Data extraction fields"
              selectionMode="multiple"
              items={dropdownOptions}
              getLabel={(option) => option.name}
              value={selectedFieldDefinitionIds}
              onChange={setSelectedFieldDefinitionIds}
              placeholder="Select fields"
              isDisabled={!allowed || isLoading || !jobTypeId}
            />
          </div>

          <div className="mt-2 text-sm text-muted-foreground">
            Select the fields that should be extracted from candidate
            interviews. You can create and manage data extraction fields from
            your{" "}
            <span
              className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
              onClick={() => {
                navigate(appPaths.settingsDataExtraction.build());
              }}
            >
              data extraction settings
            </span>
            .
          </div>

          {hasChanges && (
            <div className="mt-4">
              <SaveAndCancelButtons
                onSave={handleSave}
                onCancel={handleCancel}
                isSaveDisabled={isLoading || !jobTypeId}
              />
            </div>
          )}
        </div>
      )}
    </Can>
  );
};
